import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { JobCategory, CreateJobCategoryRequest, UpdateJobCategoryRequest } from '@/constants/types';

interface JobCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateJobCategoryRequest | UpdateJobCategoryRequest) => void;
  jobCategory?: JobCategory | null;
  isLoading?: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  description: Yup.string().required('Description is required'),
  isActive: Yup.boolean(),
});

export const JobCategoryDialog: React.FC<JobCategoryDialogProps> = ({
  open,
  onClose,
  onSubmit,
  jobCategory,
  isLoading = false,
}) => {
  const isEditMode = !!jobCategory;

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      isActive: true,
    },
    validationSchema,
    onSubmit: (values) => {
      if (isEditMode) {
        onSubmit(values as UpdateJobCategoryRequest);
      } else {
        const { isActive, ...createData } = values;
        onSubmit(createData as CreateJobCategoryRequest);
      }
    },
  });

  useEffect(() => {
    if (open) {
      if (jobCategory) {
        formik.setValues({
          name: jobCategory.name,
          description: jobCategory.description,
          isActive: jobCategory.isActive,
        });
      } else {
        formik.resetForm();
      }
    }
  }, [open, jobCategory]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isEditMode ? 'Edit Job Category' : 'Create Job Category'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              name="name"
              label="Name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              required
            />

            <TextField
              fullWidth
              name="description"
              label="Description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
              multiline
              rows={3}
              required
            />

            {isEditMode && (
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formik.values.isActive}
                    onChange={formik.handleChange}
                  />
                }
                label="Active"
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};